/**
 * SessionService – client-side session tracking for the SCS seat-cover configurator.
 *
 * Responsibilities:
 *  - Generate / restore a UUID session ID (persisted in localStorage).
 *  - Parse UTM parameters and paid-ad click IDs from the landing URL once.
 *  - Collect an ordered action log of user interactions.
 *  - Flush the accumulated session payload to the backend API via a
 *    debounced POST, with an immediate flush available for critical events
 *    (add-to-cart, configurator completed) and a sendBeacon fallback on
 *    page unload.
 *
 * Usage:
 *   import { sessionService, SessionActionType } from '../services/SessionService';
 *   sessionService.trackAction('step_change', { from: 1, to: 2 });
 *   sessionService.updateVehicle({ year: '2024', make: 'Toyota', model: 'Camry' });
 */

import { withPartNumberSkuBody } from '../helpers/helpers';

const SESSION_ID_KEY = 'scs_session_id';
const SESSION_CREATED_KEY = 'scs_session_created_at';
const SESSION_API_URL = 'https://scsbiz3.wearevisum.com/api/v1/sessions';

// Click-ID / UTM params captured from the landing-page URL. Persisted in
// sessionStorage so they survive soft navigations within the same tab.
const TRACKED_URL_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Google Ads
  'gclid',
  // Meta / Facebook Ads
  'fbclid',
  // TikTok Ads
  'ttclid',
  // Microsoft / Bing Ads
  'msclkid',
  // LinkedIn Ads
  'li_fat_id',
  // Twitter / X Ads
  'twclid',
  // Google Display & Video 360
  'dclid',
] as const;

export type TrackedUrlParam = (typeof TRACKED_URL_PARAMS)[number];

export type SessionActionType =
  | 'page_view'
  | 'step_change'
  | 'year_selected'
  | 'make_selected'
  | 'model_selected'
  | 'trim_selected'
  | 'cab_selected'
  | 'seat_selected'
  | 'color_selected'
  | 'compatibility_checked'
  | 'add_to_cart_clicked'
  | 'configurator_completed';

export interface SessionAction {
  type: SessionActionType;
  timestamp: string;
  payload?: Record<string, unknown>;
}

export interface SessionPayload {
  session_id: string;
  created_at: string;
  updated_at: string;

  // Page context
  page_url: string;
  referrer: string;
  user_agent: string;

  // Device / viewport metrics
  screen_width?: number;
  screen_height?: number;
  viewport_width?: number;
  viewport_height?: number;
  device_pixel_ratio?: number;
  color_depth?: number;
  touch_points?: number;
  language?: string;
  languages?: string;
  timezone?: string;
  platform?: string;

  // UTM / Paid-ad attribution
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  twclid?: string;
  dclid?: string;

  // Vehicle selection
  year?: string;
  make?: string;
  model?: string;
  trim?: string;
  cab?: string;

  // Product selection
  seat?: string;
  color?: string;
  selected_variant_id?: string;

  // Configurator state
  step?: number;
  configurator_complete: boolean;
  added_to_cart: boolean;

  // Full action log
  actions: SessionAction[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC-4122 v4 fallback (no external dependency)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const SESSION_STORE_KEY = 'scs_session';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

/** Read and parse the unified session store from localStorage. */
function readSessionStore(): { id: string; created_at: string; last_seen: string; url_params: Partial<Record<TrackedUrlParam, string>> } | null {
  try {
    const raw = localStorage.getItem(SESSION_STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isSessionExpired(store: { last_seen: string } | null): boolean {
  if (!store?.last_seen) return true;
  return (Date.now() - new Date(store.last_seen).getTime()) > SESSION_TIMEOUT_MS;
}

function getOrCreateSessionId(): string {
  try {
    // 1. Snippet already ran — window.scs_session_id is set synchronously
    const fromSnippet = (window as any).scs_session_id as string | undefined;
    if (fromSnippet) return fromSnippet;

    // 2. Unified store (written by scs-session-capture.js on previous pages)
    const store = readSessionStore();
    if (store?.id && !isSessionExpired(store)) return store.id;

    // 3. Legacy individual key
    const legacy = localStorage.getItem(SESSION_ID_KEY);
    if (legacy) return legacy;

    // 4. Create fresh
    const id = generateUUID();
    localStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    return generateUUID();
  }
}

function getOrCreateCreatedAt(): string {
  try {
    // Prefer unified store for consistency
    const store = readSessionStore();
    if (store?.created_at && !isSessionExpired(store)) return store.created_at;

    // Legacy key
    let ts = localStorage.getItem(SESSION_CREATED_KEY);
    if (!ts) {
      ts = new Date().toISOString();
      localStorage.setItem(SESSION_CREATED_KEY, ts);
    }
    return ts;
  } catch {
    return new Date().toISOString();
  }
}

function parseUrlParams(): Partial<Record<TrackedUrlParam, string>> {
  try {
    // 1. Snippet already ran — url_params live on window.scs_session
    const snippetSession = (window as any).scs_session as { url_params?: unknown } | undefined;
    if (snippetSession?.url_params && typeof snippetSession.url_params === 'object') {
      return snippetSession.url_params as Partial<Record<TrackedUrlParam, string>>;
    }

    // 2. Unified localStorage store (written by snippet on a previous page)
    const store = readSessionStore();
    if (store?.url_params && !isSessionExpired(store)) return store.url_params;

    // 3. Parse fresh from the current URL
    const result: Partial<Record<TrackedUrlParam, string>> = {};
    const sp = new URLSearchParams(window.location.search);
    TRACKED_URL_PARAMS.forEach((key) => {
      const val = sp.get(key);
      if (val) result[key] = val;
    });
    return result;
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// SessionService class
// ---------------------------------------------------------------------------

class SessionService {
  private readonly sessionId: string;
  private readonly createdAt: string;
  private readonly urlParams: Partial<Record<TrackedUrlParam, string>>;

  private vehicle: Pick<SessionPayload, 'year' | 'make' | 'model' | 'trim' | 'cab'> = {};
  private product: Pick<SessionPayload, 'seat' | 'color' | 'selected_variant_id'> = {};
  private step: number | undefined = undefined;
  private configuratorComplete = false;
  private addedToCart = false;

  private actions: SessionAction[] = [];
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly DEBOUNCE_MS = 2000;
  private readonly MAX_COALESCE_MS = 30_000;
  private lastFlushAt = 0;

  constructor() {
    this.sessionId = getOrCreateSessionId();
    this.createdAt = getOrCreateCreatedAt();
    this.urlParams = parseUrlParams();

    // Register page-unload flush (best-effort via sendBeacon)
    if (typeof window !== 'undefined') {
      window.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('pagehide', this.flushBeacon);
    }
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  getSessionId(): string {
    return this.sessionId;
  }

  trackAction(type: SessionActionType, payload?: Record<string, unknown>): void {
    this.actions.push({
      type,
      timestamp: new Date().toISOString(),
      ...(payload !== undefined ? { payload } : {}),
    });

    // Auto-set status flags for critical events
    if (type === 'add_to_cart_clicked') {
      this.addedToCart = true;
    }
    if (type === 'configurator_completed') {
      this.configuratorComplete = true;
    }

    // Immediate flush for critical conversion events
    if (type === 'add_to_cart_clicked' || type === 'configurator_completed') {
      this.flushImmediate();
    } else {
      this.scheduleFlush();
    }
  }

  updateVehicle(data: { year?: string; make?: string; model?: string; trim?: string; cab?: string }): void {
    this.vehicle = { ...this.vehicle, ...data };
    this.scheduleFlush();
  }

  updateProduct(data: { seat?: string; color?: string; selected_variant_id?: string }): void {
    this.product = { ...this.product, ...data };
    this.scheduleFlush();
  }

  updateStep(step: number): void {
    this.step = step;
    this.scheduleFlush();
  }

  setConfiguratorComplete(complete: boolean): void {
    this.configuratorComplete = complete;
    this.scheduleFlush();
  }

  // -------------------------------------------------------------------------
  // Payload builder
  // -------------------------------------------------------------------------

  buildPayload(): SessionPayload {
    return {
      session_id: this.sessionId,
      created_at: this.createdAt,
      updated_at: new Date().toISOString(),

      page_url: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',

      // Device / viewport metrics
      ...(typeof screen !== 'undefined' ? { screen_width: screen.width, screen_height: screen.height } : {}),
      ...(typeof window !== 'undefined' ? { viewport_width: window.innerWidth, viewport_height: window.innerHeight, device_pixel_ratio: window.devicePixelRatio ?? 1 } : {}),
      ...(typeof screen !== 'undefined' ? { color_depth: screen.colorDepth } : {}),
      ...(typeof navigator !== 'undefined' ? {
        touch_points: navigator.maxTouchPoints ?? 0,
        language: navigator.language ?? '',
        languages: Array.from(navigator.languages ?? []).join(','),
        platform: navigator.platform ?? '',
      } : {}),
      ...(typeof Intl !== 'undefined' ? { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone } : {}),

      ...this.urlParams,
      ...this.vehicle,
      ...this.product,

      ...(this.step !== undefined ? { step: this.step } : {}),
      configurator_complete: this.configuratorComplete,
      added_to_cart: this.addedToCart,

      actions: [...this.actions],
    };
  }

  // -------------------------------------------------------------------------
  // Flush strategies
  // -------------------------------------------------------------------------

  private scheduleFlush(): void {
    const now = Date.now();

    // Force flush if we've been accumulating for too long
    if (this.lastFlushAt > 0 && now - this.lastFlushAt >= this.MAX_COALESCE_MS) {
      this.flushImmediate();
      return;
    }

    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.flushImmediate();
    }, this.DEBOUNCE_MS);
  }

  flushImmediate(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.lastFlushAt = Date.now();
    this.sendToApi(this.buildPayload()).catch(() => {
      // Silently swallow – tracking must never break the user flow
    });
  }

  private flushBeacon = (): void => {
    try {
      const payload = JSON.stringify(withPartNumberSkuBody(this.buildPayload()));
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(SESSION_API_URL, new Blob([payload], { type: 'application/json' }));
      }
    } catch {
      // Non-fatal
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flushBeacon();
    }
  };

  // -------------------------------------------------------------------------
  // Network
  // -------------------------------------------------------------------------

  private async sendToApi(payload: SessionPayload): Promise<void> {
    await fetch(SESSION_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withPartNumberSkuBody(payload)),
      // Non-blocking credential policy – no cookies sent cross-origin
      credentials: 'omit',
    });
  }

  // For testing only – resets action log without touching identity
  _resetActionsForTest(): void {
    this.actions = [];
    this.vehicle = {};
    this.product = {};
    this.step = undefined;
    this.configuratorComplete = false;
    this.addedToCart = false;
    this.lastFlushAt = 0;
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}

// Singleton export so the same instance is used across the whole app
export const sessionService = new SessionService();
